import { useEffect, useState } from "react";
import { Button } from "@/src/components/tailwind/Button";
import { H3 } from "@/src/components/Heading";
import useGqlClient from "@/src/hooks/useGqlClient";
import {
  DealGroup,
  DealRole,
  DealRoleType,
  useCreateDealInviteMutation,
} from "@/src/graphql/generated";
import { toasts } from "@/src/components/toasts/toasts";
import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import {
  DealRoleOption,
  DealRolesDropdown,
} from "@/src/components/permissions/DealRolesDropdown";
import { EmailPillsInput } from "@/src/components/EmailPillsInput";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dealGroup: DealGroup;
  dealId: string;
  roleTypes: DealRoleType[];
  inviteeRole?: DealRole;
}

export function InviteMoreToGroup({
  isOpen,
  onClose,
  dealGroup,
  dealId,
  inviteeRole,
  roleTypes,
}: Props) {
  const client = useGqlClient();
  const createInvite = useCreateDealInviteMutation(client);

  const roleType = roleTypes[0] ?? DealRoleType.Guest;

  const [emails, setEmails] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<DealRoleOption | null>(null);
  const [debouncedEmails, setDebouncedEmails] = useState<string[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEmails(emails), 600);
    return () => clearTimeout(timer);
  }, [emails]);

  function close() {
    setEmails([]);
    setSelectedRole(null);
    onClose();
  }

  const handleSubmit = async () => {
    if (!selectedRole) {
      return;
    }

    try {
      await Promise.all(
        emails
          .filter((email) => email.includes("@"))
          .map(
            async (email) =>
              await createInvite.mutate({
                input: {
                  autoAdmit: false,
                  email,
                  dealGroupID: dealGroup.id,
                  dataRoomPermissionID: dealGroup.dataRoomPermission.id,
                  dealID: dealId,
                  filePermissions: [],
                  folderPermissions: [],
                  message: "",
                  overrideDataRoomAccess: false,
                  dealRoleID: selectedRole.id,
                },
              }),
          ),
      );

      toasts.success("Invites sent successfully");
      onClose();
      setEmails([]);
      setSelectedRole(null);
    } catch (error) {
      console.log(error);
      toasts.error("Failed to send invites");
    }
  };

  if (!dealGroup) {
    return null;
  }

  const existingDomains = dealGroup.accounts
    .map((a) => a.account.email.split("@")[1])
    .filter(Boolean) as string[];

  const showDomainWarning =
    existingDomains.length > 0 &&
    debouncedEmails.some((email) => {
      const domain = email.includes("@") ? email.split("@")[1] : null;
      return domain && !existingDomains.includes(domain);
    });

  return (
    <AnimatedModal open={isOpen} onClose={close} size="xl">
      <div>
        <div className="flex items-center justify-between">
          <H3>Invite more users to {dealGroup.name}</H3>
          <CloseIcon onClose={close} />
        </div>

        <div className="mt-4">
          <p className="text-sm font-medium text-gray-900">Emails</p>
          <EmailPillsInput
            className="mt-1"
            emails={emails}
            onChange={setEmails}
          />
          <div className="mt-3">
            <p className="text-sm font-semibold text-persian-990">Role</p>
            <p className="text-sm text-concrete-600 leading-tight">
              This role applies to everyone invited above.
            </p>
            <div className="mt-2">
              <DealRolesDropdown
                dealId={dealId}
                roleType={roleType}
                value={selectedRole?.id ?? inviteeRole?.id}
                onChange={setSelectedRole}
                size="s"
              />
            </div>
          </div>
          {showDomainWarning && (
            <div className="border border-orange-400 bg-orange-400/20 rounded-md p-2 mt-2">
              <p className="text-orange-700 text-sm font-semibold">Warning</p>
              <p className="text-orange-700 text-sm">
                The email domain of this user does not match the email domain of
                users already in this group. Be sure you are adding the user to
                the right group.
              </p>
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-x-2">
          <Button text="Cancel" variant="neutral" onClick={close} />
          <Button
            text="Send Invites"
            variant="positive"
            onClick={handleSubmit}
            isDisabled={emails.length === 0 || !selectedRole}
            isLoading={createInvite.isPending}
          />
        </div>
      </div>
    </AnimatedModal>
  );
}
